// src/restaurant/pages/Reports.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { useLanguage } from '../../contexts/LanguageContext';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import { LineChart, Line, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import './Reports.css';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addArabicFont } from '../../assets/fonts/pdfArabicSupport';

const Reports = () => {
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('week');
  const [reportType, setReportType] = useState('orders');
  const [chartData, setChartData] = useState([]);
  const [paymentMethodData, setPaymentMethodData] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    cancelledOrders: 0,
    totalRevenue: 0,
    totalDeliveryFees: 0,
    avgOrderValue: 0
  });

  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const formatDate = useCallback((date) => {
    const options = { day: 'numeric', month: 'short' };
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', options);
  }, [language]);

  const getDayOfWeek = useCallback((date) => {
    const options = { weekday: 'short' };
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', options);
  }, [language]);

  const getWeekNumber = useCallback((date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  }, []);

  const prepareChartData = useCallback((ordersData) => {
    const dataMap = {};
    if (dateRange === 'day') {
      for (let hour = 0; hour < 24; hour++) {
        const timeLabel = `${hour}:00`;
        dataMap[timeLabel] = { time: timeLabel, orders: 0, revenue: 0 };
      }
      ordersData.forEach(order => {
        const hour = order.date.getHours();
        const timeLabel = `${hour}:00`;
        if (dataMap[timeLabel]) {
          dataMap[timeLabel].orders += 1;
          dataMap[timeLabel].revenue += (order.amount || 0);
        }
      });
    } else {
      const today = new Date();
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(today.getDate() - i);
        const dateKey = formatDate(date);
        dataMap[dateKey] = { time: dateKey, orders: 0, revenue: 0 };
      }
      ordersData.forEach(order => {
        const dateKey = order.dateFormatted;
        if (dataMap[dateKey]) {
          dataMap[dateKey].orders += 1;
          dataMap[dateKey].revenue += (order.amount || 0);
        }
      });
    }
    setChartData(Object.values(dataMap).map(item => ({
      time: item.time,
      orders: item.orders,
      revenue: parseFloat(item.revenue.toFixed(3))
    })));
  }, [dateRange, formatDate]);

  const preparePaymentMethodData = useCallback((ordersData) => {
    const methods = {};
    ordersData.forEach(order => {
      const method = order.paymentMethod || t('unknown');
      methods[method] = (methods[method] || 0) + 1;
    });
    setPaymentMethodData(Object.entries(methods).map(([name, value]) => ({ name, value })));
  }, [t]);

  const prepareOrderStatusData = useCallback((ordersData) => {
    const statuses = {};
    ordersData.forEach(order => {
      const status = order.status || t('unknown');
      statuses[status] = (statuses[status] || 0) + 1;
    });
    setOrderStatusData(Object.entries(statuses).map(([name, value]) => ({ name, value })));
  }, [t]);

  const calculateStats = useCallback((ordersData) => {
    const totalOrders = ordersData.length;
    const completedOrders = ordersData.filter(order => order.status === 'تم التسليم ✅').length;
    const cancelledOrders = ordersData.filter(order => order.status === 'ملغي ❌').length;
    const totalRevenue = ordersData.filter(order => order.status === 'تم التسليم ✅').reduce((sum, order) => sum + (order.amount || 0), 0);
    const totalDeliveryFees = ordersData.reduce((sum, order) => sum + (order.deliveryFee || 0), 0);
    const avgOrderValue = completedOrders > 0 ? totalRevenue / completedOrders : 0;
    setStats({ totalOrders, completedOrders, cancelledOrders, totalRevenue, totalDeliveryFees, avgOrderValue });
  }, []);

  const fetchData = useCallback(async (restaurantId) => {
    try {
      setLoading(true);
      const startDate = getStartDateFromRange(dateRange);

      const ordersRef = collection(db, 'orders');
      const ordersQuery = query(
        ordersRef,
        where('restaurantId', '==', restaurantId),
        where('createdAt', '>=', startDate),
        orderBy('createdAt', 'desc')
      );

      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
  id: doc.id,
  // use the sequential code stored in Firestore
  orderCode: doc.data().orderCode || doc.id,
  ...doc.data(),
  date: doc.data().createdAt ? doc.data().createdAt.toDate() : new Date()
}));


      const enhancedOrders = ordersData.map(order => ({
        ...order,
        dayOfWeek: getDayOfWeek(order.date),
        dateFormatted: formatDate(order.date),
        month: order.date.getMonth(),
        week: getWeekNumber(order.date),
        dayNumber: order.date.getDate()
      }));

      setOrders(enhancedOrders);

      const transactionsRef = collection(db, 'transactions');
      const transactionsQuery = query(
        transactionsRef,
        where('restaurantId', '==', restaurantId),
        where('timestamp', '>=', startDate),
        orderBy('timestamp', 'desc')
      );

      const transactionsSnapshot = await getDocs(transactionsQuery);
      const transactionsData = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().timestamp ? doc.data().timestamp.toDate() : new Date(),
        dateFormatted: doc.data().timestamp ? formatDate(doc.data().timestamp.toDate()) : formatDate(new Date())
      }));

      setTransactions(transactionsData);
      prepareChartData(enhancedOrders);
      preparePaymentMethodData(enhancedOrders);
      prepareOrderStatusData(enhancedOrders);
      calculateStats(enhancedOrders);

    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, formatDate, getDayOfWeek, getWeekNumber, prepareChartData, preparePaymentMethodData, prepareOrderStatusData, calculateStats]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/restaurant/login');
        return;
      }
      const restaurantId = localStorage.getItem('restaurantId');
      if (!restaurantId) {
        navigate('/restaurant/login');
        return;
      }
      await fetchData(restaurantId);
    });
    return () => unsubscribe();
  }, [navigate, fetchData]);

  useEffect(() => {
    const restaurantId = localStorage.getItem('restaurantId');
    if (restaurantId) {
      fetchData(restaurantId);
    }
  }, [dateRange, reportType, fetchData]);

  const getStartDateFromRange = (range) => {
    const now = new Date();
    const startDate = new Date();
    switch(range) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
    }
    return startDate;
  };

  const formatCurrency = (amount) => `${amount.toFixed(3)} د.ك`;

  const getRangeTitle = () => {
    switch(dateRange) {
      case 'day': return t('today');
      case 'week': return t('last_week');
      case 'month': return t('last_month');
      case 'year': return t('last_year');
      default: return t('last_week');
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  if (loading) return <div className="restaurant-loading"><div className="restaurant-spinner"></div><p>{t('loading')}</p></div>;


  const exportToExcel = () => {
    let data = [];
  
    if (reportType === 'orders') {
        data = orders.map(o => ({
      'رقم الطلب':      o.orderCode,
      'اسم العميل':    o.customerName,
      'الهاتف':        o.phone,
      'العنوان':       o.address || '',
      'المبلغ':        o.amount?.toFixed(3) || '',
      'رسوم التوصيل':  o.deliveryFee?.toFixed(3) || '',
      'الحالة':        o.status,
      'التاريخ':       o.dateFormatted
    }));
  } else {
    data = transactions.map(tx => ({
      'التاريخ':        tx.dateFormatted,
      'الوصف':         tx.description,
      'نوع العملية':   tx.type,               // 'شحن' أو 'خصم'
      'طريقة الدفع':    tx.paymentMethod || '-',
      'المبلغ':         (tx.type === 'شحن' ? '+' : '-') + tx.amount.toFixed(3)
    }));
  }
  
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
  
    XLSX.writeFile(workbook, `${reportType}_report.xlsx`);
  };
  
  
  
  const exportToPDF = () => {
  const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
  addArabicFont(doc);
  doc.setFontSize(14);
  doc.setTextColor(40);

  // عنوان التقرير حسب النوع
  const title = reportType === 'orders' 
    ? 'تقرير الطلبات' 
    : 'تقرير المعاملات المالية';
  doc.text(title, 140, 20, { align: 'center' });

  let headers = [];
  let rows = [];

  if (reportType === 'orders') {
    // أعمدة الطلبات مع orderCode
    headers = ['رقم الطلب', 'اسم العميل', 'الهاتف', 'العنوان', 'المبلغ', 'رسوم التوصيل', 'الحالة', 'التاريخ'];
    rows = orders.map(order => [
      order.orderCode || order.id,            // use orderCode if available
      order.customerName,
      order.phone,
      order.address || '',
      order.amount?.toFixed(3) || '',
      order.deliveryFee?.toFixed(3) || '',
      order.status,
      order.dateFormatted
    ]);
  } else {
    // أعمدة المعاملات المالية
    headers = ['التاريخ', 'الوصف', 'نوع العملية', 'طريقة الدفع', 'المبلغ'];
    rows = transactions.map(tx => [
      tx.dateFormatted,
      tx.description,
      tx.type,                                    // 'شحن' أو 'خصم'
      tx.paymentMethod || '-',
      (tx.type === 'شحن' ? '+' : '-') + ' ' + tx.amount.toFixed(3) + ' د.ك'
    ]);
  }

  autoTable(doc, {
    head: [headers],
    body: rows,
    styles: {
      font: 'Amiri',
      fontStyle: 'normal',
      fontSize: 10,
      halign: 'right',
    },
    headStyles: {
      fillColor: [40, 60, 90],
      textColor: 255,
      fontSize: 12
    },
    margin: { top: 30 }
  });

  // اسم الملف يتغيّر حسب النوع
  const filename = reportType === 'orders' 
    ? 'orders_report.pdf' 
    : 'finance_report.pdf';
  doc.save(filename);
};

  
  
  

  return (
    <div className="restaurant-reports-page">
      <RestaurantSidebar />
      <div className="restaurant-reports-content">
        <RestaurantHeader restaurantName={localStorage.getItem('restaurantName') || t('restaurant')} />
        
        <div className="reports-container">
          <div className="reports-header">
            <h1>{t('reports_statistics')}</h1>
            <div className="reports-controls">
            <div className="export-buttons">
  <button onClick={exportToExcel} className="export-btn">📥 {t('export_excel') || 'استخراج Excel'}</button>
  <button onClick={exportToPDF} className="export-btn">📄 {t('export_pdf') || 'استخراج PDF'}</button>
</div>

              <div className="range-selector">
                <label>{t('time_period')}:</label>
                <select 
                  value={dateRange} 
                  onChange={(e) => setDateRange(e.target.value)}
                >
                  <option value="day">{t('today')}</option>
                  <option value="week">{t('last_week')}</option>
                  <option value="month">{t('last_month')}</option>
                  <option value="year">{t('last_year')}</option>
                </select>
              </div>
              
              <div className="report-type-tabs">
                <button 
                  className={`report-tab ${reportType === 'orders' ? 'active' : ''}`}
                  onClick={() => setReportType('orders')}
                >
                  {t('orders_report')}
                </button>
                <button 
                  className={`report-tab ${reportType === 'finance' ? 'active' : ''}`}
                  onClick={() => setReportType('finance')}
                >
                  {t('financial_report')}
                </button>
              </div>
            </div>
          </div>
          
          <div className="report-stats-cards">
            {reportType === 'orders' ? (
              <>
                <div className="stat-card">
                  <div className="stat-value">{stats.totalOrders}</div>
                  <div className="stat-label">{t('total_orders')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">{stats.completedOrders}</div>
                  <div className="stat-label">{t('completed_orders')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">{stats.cancelledOrders}</div>
                  <div className="stat-label">{t('cancelled_orders')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">
                    {(stats.completedOrders / (stats.totalOrders || 1) * 100).toFixed(1)}%
                  </div>
                  <div className="stat-label">{t('completion_rate')}</div>
                </div>
              </>
            ) : (
              <>
                <div className="stat-card">
                  <div className="stat-value">{formatCurrency(stats.totalRevenue)}</div>
                  <div className="stat-label">{t('total_sales')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">{formatCurrency(stats.totalDeliveryFees)}</div>
                  <div className="stat-label">{t('delivery_fees')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">{formatCurrency(stats.avgOrderValue)}</div>
                  <div className="stat-label">{t('avg_order_value')}</div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-value">
                    {formatCurrency(stats.totalRevenue - stats.totalDeliveryFees)}
                  </div>
                  <div className="stat-label">{t('net_sales')}</div>
                </div>
              </>
            )}
          </div>
          
          {/* أضف هنا الرسوم البيانية */}
          <div className="report-chart-section">
            <h2>{reportType === 'orders' ? t('orders_over_time') : t('revenue_over_time')} - {getRangeTitle()}</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip formatter={(value, name) => {
                  return name === 'orders' ? `${value} ${t('order')}` : `${value} ${t('currency')}`;
                }} />
                <Legend formatter={(value) => value === 'orders' ? t('orders') : t('revenue')} />
                <Line
                  type="monotone"
                  dataKey="orders"
                  stroke="#4a90e2"
                  name="orders"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
                {reportType === 'finance' && (
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#28a745"
                    name="revenue"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          {/* رسم بياني توزيع طرق الدفع والحالات */}
          <div className="charts-row">
            <div className="report-chart-section chart-half">
              <h2>{t('payment_methods')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={paymentMethodData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {paymentMethodData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} ${t('orders')}`, t('quantity')]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="report-chart-section chart-half">
              <h2>{t('order_statuses')}</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    outerRadius={80}
                    fill="#8684d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} ${t('orders')}`, t('quantity')]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="report-data-section">
            <h2>{reportType === 'orders' ? t('orders_details') : t('financial_transactions')} - {getRangeTitle()}</h2>
            
            {reportType === 'orders' ? (
              orders.length === 0 ? (
                <div className="no-data">
                  <p>{t('no_orders_in_period')}</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>{t('order_number')}</th>
                        <th>{t('date')}</th>
                        <th>{t('customer')}</th>
                        <th>{t('payment_method')}</th>
                        <th>{t('amount')}</th>
                        <th>{t('delivery_fee')}</th>
                        <th>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <tr key={order.id} onClick={() => navigate(`/restaurant/orders/${order.id}`)}>
                        <td className="order-id">{order.orderCode.slice(0, 8)}...</td>
                          <td>{order.dateFormatted}</td>
                          <td>{order.customerName}</td>
                          <td>{order.paymentMethod}</td>
                          <td>{order.amount?.toFixed(3)} د.ك</td>
                          <td>{order.deliveryFee?.toFixed(3)} د.ك</td>
                          <td>
                            <span className={`status-badge ${
                              order.status === 'تم التسليم ✅' ? 'completed' : 
                              order.status === 'ملغي ❌' ? 'cancelled' : 'pending'
                            }`}>
                              {order.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              transactions.length === 0 ? (
                <div className="no-data">
                  <p>{t('no_transactions_in_period')}</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>{t('date')}</th>
                        <th>{t('description')}</th>
                        <th>{t('type')}</th>
                        <th>{t('payment_method')}</th>
                        <th>{t('amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>{transaction.dateFormatted}</td>
                          <td>{transaction.description}</td>
                          <td>
                            <span className={`transaction-type ${transaction.type === 'شحن' ? 'deposit' : 'deduction'}`}>
                              {transaction.type === 'شحن' ? t('deposit') : t('deduction')}
                            </span>
                          </td>
                          <td>{transaction.paymentMethod || '-'}</td>
                          <td className={transaction.type === 'شحن' ? 'positive-amount' : 'negative-amount'}>
                            {transaction.type === 'شحن' ? '+' : '-'} {transaction.amount?.toFixed(3)} د.ك
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;